/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';

const vmsSessionInitState = {
  XPMobileSDK: null,
  XPMobileSDKSettings: null,
  loggingIn: false,
  loading: true,
  loadingData: false,
  isLogged: false,
  isConnected: false,
  connecting: false,
  error: null,
  cameraList: {},
  attempts: 10,
  // Se marca cuando el SDK no está disponible; permite que la UI degrade
  // (mostrar "VMS no disponible") en vez de tumbar la aplicación.
  unavailable: false,
};

const VMS_URL = process.env.REACT_APP_VMS_URL;
const VMS_USER = process.env.REACT_APP_VMS_USER;
const VMS_PASS = process.env.REACT_APP_VMS_PASS;
const VMS_AUTH_TYPE = process.env.REACT_APP_VMS_AUTH_TYPE || 'ActiveDirectory';

export default function useVmsAuth(user) {
  const [vmsSession, setVmsSession] = useState(vmsSessionInitState);

  const connectionFunctions = {
    connectionDidConnect: () =>
      setVmsSession((prevState) => {
        return {
          ...prevState,
          connecting: false,
          isConnected: true,
          loggingIn: true,
          isLogged: false,
          error: null,
        };
      }),
    connectionFailedToConnect: () =>
      setVmsSession((prevState) => {
        return {
          ...prevState,
          error: 'Failed To Connect with Mobile Server',
          attempts: prevState.attempts - 1,
        };
      }),
    connectionDidLogIn: () =>
      setVmsSession((prevState) => {
        return {
          ...prevState,
          loggingIn: false,
          isLogged: true,
          loadingData: true,
          error: null,
        };
      }),
    connectionFailedToLogIn: () =>
      setVmsSession((prevState) => {
        return {
          ...prevState,
          error: 'Failed To Login into Mobile Server',
          attempts: prevState.attempts - 1,
        };
      }),
    connectionLostConnection: () =>
      setVmsSession({
        ...vmsSessionInitState,
        XPMobileSDK: window.XPMobileSDK,
        XPMobileSDKSettings: window.XPMobileSDKSettings,
        loading: false,
        connecting: true,
        isConnected: false,
        error: 'Disconnected from the server',
      }),
  };

  useEffect(() => {
    // El SDK se carga por <script> en public/index.html. Si no está disponible
    // (script caído, bloqueado por red, o servidor VMS inalcanzable) no se debe
    // propagar el error: este hook vive dentro del AuthProvider raíz y una
    // excepción aquí dejaría la plataforma entera sin renderizar.
    const loadMobileSdk = window.LoadMobileSdk;

    // .env está en .gitignore: si el entorno no trae las variables del VMS,
    // se desactiva la integración en vez de intentar conectar contra el origen
    // de la app (window.location.origin) y agotar los 10 reintentos.
    if (!VMS_URL || !VMS_USER || !VMS_PASS) {
      console.warn(
        'VMS: faltan REACT_APP_VMS_URL / _USER / _PASS en el entorno; cámaras deshabilitadas.'
      );
      setVmsSession((prevState) => ({
        ...prevState,
        loading: false,
        connecting: false,
        unavailable: true,
        error: 'Configuración del VMS incompleta',
      }));
      return;
    }

    if (typeof loadMobileSdk !== 'function') {
      console.warn(
        'VMS: XPMobileSDK no está disponible; las cámaras quedarán deshabilitadas.'
      );
      setVmsSession((prevState) => ({
        ...prevState,
        loading: false,
        connecting: false,
        unavailable: true,
        error: 'SDK del VMS no disponible',
      }));
      return;
    }

    try {
      loadMobileSdk(onLoadSdk);
    } catch (error) {
      console.error('VMS: error al cargar el SDK', error);
      setVmsSession((prevState) => ({
        ...prevState,
        loading: false,
        connecting: false,
        unavailable: true,
        error: 'No se pudo cargar el SDK del VMS',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (vmsSession.unavailable || vmsSession.attempts === 0) return;
    if (!vmsSession.loading && vmsSession.connecting) {
      connectToServer(connectionFunctions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vmsSession.loading,
    vmsSession.connecting,
    vmsSession.error,
    vmsSession.attempts,
  ]);

  useEffect(() => {
    if (vmsSession.unavailable || vmsSession.attempts === 0) return;
    if (user && vmsSession.loggingIn) {
      loginCommand({
        user: VMS_USER,
        pass: VMS_PASS,
        type: VMS_AUTH_TYPE,
      });
    }
    if (!user && vmsSession.isLogged) {
      vmsSession.XPMobileSDK?.disconnect?.();
      setVmsSession({
        ...vmsSessionInitState,
        XPMobileSDK: window.XPMobileSDK,
        XPMobileSDKSettings: window.XPMobileSDKSettings,
        loading: false,
        connecting: true,
        isConnected: false,
      });
    }
  }, [user, vmsSession.loggingIn, vmsSession.error, vmsSession.attempts]);

  useEffect(() => {
    if (vmsSession.unavailable || vmsSession.attempts === 0) return;
    if (vmsSession.isLogged && vmsSession.loadingData) {
      loadCamerasInfo();
    }
  }, [
    vmsSession.isLogged,
    vmsSession.loadingData,
    vmsSession.error,
    vmsSession.attempts,
  ]);

  const onLoadSdk = () => {
    if (!window.XPMobileSDK || !window.XPMobileSDKSettings) {
      console.warn('VMS: el SDK cargó incompleto; cámaras deshabilitadas.');
      setVmsSession((prevState) => ({
        ...prevState,
        loading: false,
        connecting: false,
        unavailable: true,
        error: 'SDK del VMS incompleto',
      }));
      return;
    }

    window.XPMobileSDKSettings.MobileServerURL = VMS_URL;
    window.XPMobileSDKSettings.EnableConsoleLog = false;
    window.logger?.init?.();
    setVmsSession((prevState) => {
      return {
        ...prevState,
        XPMobileSDK: window.XPMobileSDK,
        XPMobileSDKSettings: window.XPMobileSDKSettings,
        loading: false,
        connecting: true,
        isConnected: false,
      };
    });
  };

  const connectToServer = (connectionFunctions) => {
    // Connect to the desired server (defaults to the current URL)
    const url =
      vmsSession.XPMobileSDKSettings.MobileServerURL || window.location.origin;

    let lastObserver;
    if (lastObserver) {
      vmsSession.XPMobileSDK.removeObserver(lastObserver);
    }

    lastObserver = connectionFunctions;
    vmsSession.XPMobileSDK.addObserver(lastObserver);
    vmsSession.XPMobileSDK.connect(url);
  };

  const loginCommand = (credentials) => {
    vmsSession.XPMobileSDK.login(
      credentials.user,
      credentials.pass,
      credentials.type,
      {
        SupportsAudioIn: 'Yes',
        SupportsAudioOut: 'Yes',
      }
    );
  };

  const loadCamerasInfo = () => {
    vmsSession.XPMobileSDK.getAllViews((items) => {
      if (!items) {
        setVmsSession((prevState) => {
          return {
            ...prevState,
            error: 'Failed to get views capabilities',
            attempts: prevState.attempts - 1,
          };
        });
        return;
      }
      // La estructura de vistas del VMS viene muy anidada; si cambia o llega
      // incompleta no debe lanzar, solo reportarse como error de sesión.
      const cameras = items?.[0]?.Items?.[0]?.Items?.[0]?.Items;
      if (!Array.isArray(cameras)) {
        console.warn('VMS: estructura de vistas inesperada', items);
        setVmsSession((prevState) => {
          return {
            ...prevState,
            error: 'Unexpected views structure',
            attempts: prevState.attempts - 1,
          };
        });
        return;
      }

      let cameraList = {};
      for (let i = 0; i < cameras.length; i++) {
        const camera = cameras[i];
        const hasPTZ = camera.PTZ === 'Yes';
        const subItems = Array.isArray(camera.Items) ? camera.Items : [];
        const hasMicro = subItems.some((curr) => curr.Type === 'Microphone');
        const hasSpeaker = subItems.some((curr) => curr.Type === 'Speaker');
        cameraList[camera.Id] = { ...camera, hasPTZ, hasMicro, hasSpeaker };
      }
      setVmsSession((prevState) => {
        return {
          ...prevState,
          loadingData: false,
          cameraList: cameraList,
          attempts: 10,
          error: null,
        };
      });
    });
  };

  return {
    vmsSession,
  };
}
